import { AsyncPipe, DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, effect, ElementRef, OnDestroy, OnInit, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable } from 'rxjs';
import { ContextMenuComponent, ContextMenuItem } from '../../context-menu/context-menu.component';
import { SettingsUsersState, SettingsUsersStore } from './settings-users.store';
import { MemberRowVm } from './models/member-row.view-model';
import { MemberRole, PasswordLinkPurpose } from './models/member.dto';

/**
 * Ustawienia → Użytkownicy (0058): the tenant runs its own people. One table, an inline invitation
 * form above it (the app has no modal), the row menu for everything else, and the one-time link
 * shown in a panel that forgets it on close. First screen of the tenant's admin area; Subskrypcja
 * joins it in slice B2.
 */
@Component({
  selector: 'settings-users-page',
  templateUrl: './settings-users.page.html',
  styleUrl: './settings-users.page.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AsyncPipe, DatePipe, ContextMenuComponent]
})
export class SettingsUsersPage implements OnInit, OnDestroy {
  readonly state$: Observable<SettingsUsersState>;

  readonly editingMemberId = signal<number | undefined>(undefined);
  readonly editInput = viewChild<ElementRef<HTMLInputElement>>('editInput');

  readonly nameInput = viewChild<ElementRef<HTMLInputElement>>('nameInput');

  readonly linkCopied = signal(false);

  // the list's shadow while it loads (0128): five rows at the row's height
  readonly shadowRows = [0, 1, 2, 3, 4];

  constructor(
    private store: SettingsUsersStore,
    private destroyRef: DestroyRef
  ) {
    this.state$ = this.store.state$;

    // the inputs only exist while editing or inviting — focus them as soon as they appear (client-order precedent)
    effect(() => {
      const input = this.editInput() ?? this.nameInput();

      if (input) {
        input.nativeElement.focus();
        input.nativeElement.select();
      }
    });
  }

  ngOnInit(): void {
    this.store.init().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  ngOnDestroy(): void {
    this.store.destroy();
  }

  // --- Zaproś użytkownika ---

  openInvite(): void {
    this.store.openInvite();
  }

  closeInvite(): void {
    this.store.closeInvite();
  }

  // Enter submits the form natively; the handler owns the rest (sign-in precedent)
  onInviteSubmit(event: Event, displayName: string, email: string, role: string, isAdmin: boolean): void {
    event.preventDefault();

    const trimmedName = displayName.trim();
    const trimmedEmail = email.trim();

    if (!trimmedName || !trimmedEmail || this.store.state.inviting) {
      return;
    }

    this.store.invite({ displayName: trimmedName, email: trimmedEmail, role: role as MemberRole, isAdmin })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  // --- the link panel ---

  linkValidity(purpose: PasswordLinkPurpose): string {
    return purpose === 'Invite'
      ? 'Link działa tylko raz i jest ważny 7 dni.'
      : 'Link działa tylko raz i jest ważny 24 godziny.';
  }

  copyLink(linkUrl: string): void {
    navigator.clipboard.writeText(linkUrl).then(() => this.linkCopied.set(true));
  }

  closeLink(): void {
    this.linkCopied.set(false);
    this.store.closeLink();
  }

  // --- the name, edited in place (client-order header precedent) ---

  startEditing(member: MemberRowVm): void {
    this.editingMemberId.set(member.id);
  }

  stopEditing(): void {
    this.editingMemberId.set(undefined);
  }

  saveName(member: MemberRowVm, value: string): void {
    this.stopEditing();

    const displayName = value.trim();

    if (!displayName || displayName === member.displayName) {
      return;
    }

    this.store.rename(member.id, displayName).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  // --- the row menu: each action only when it would succeed ---

  rowMenuItems(state: SettingsUsersState): ContextMenuItem[] {
    const member = state.members?.find(x => x.id === state.rowMenu?.memberId);

    if (!member) {
      return [];
    }

    const items: ContextMenuItem[] = [];

    if (member.status === 'Deactivated') {
      items.push({ id: 'activate', label: 'Aktywuj' });

      return items;
    }

    items.push(member.role === 'Planner'
      ? { id: 'role-foreman', label: 'Zmień rolę na Brygadzista' }
      : { id: 'role-planner', label: 'Zmień rolę na Planista' });

    if (!member.isAdmin) {
      items.push({ id: 'grant-admin', label: 'Nadaj uprawnienia administratora' });
    } else if (!member.isYou && !member.isLastActiveAdmin) {
      items.push({ id: 'revoke-admin', label: 'Odbierz uprawnienia administratora' });
    }

    items.push({ id: 'password-link', label: 'Wygeneruj link do ustawienia hasła' });

    if (member.lockedUntil) {
      items.push({ id: 'unlock', label: 'Odblokuj konto' });
    }

    if (!member.isYou && !member.isLastActiveAdmin) {
      items.push({ id: 'deactivate', label: 'Dezaktywuj' });
    }

    return items;
  }

  onRowContextMenu(event: MouseEvent, member: MemberRowVm): void {
    // Right-click at (almost) the same point again: close our menu and let the native one
    // through — the same bypass as the BOM tree node and the overseer header
    const openAt = this.store.state.rowMenu?.position;
    const samePoint = openAt
      && Math.abs(openAt.x - event.clientX) < 10
      && Math.abs(openAt.y - event.clientY) < 10;

    if (samePoint) {
      this.store.closeRowMenu();
      return;
    }

    event.preventDefault();

    this.store.openRowMenu(member.id, { x: event.clientX, y: event.clientY });
  }

  onRowMenuAction(actionId: string): void {
    const memberId = this.store.state.rowMenu!.memberId;

    const action = (() => {
      switch (actionId) {
        case 'role-planner': return this.store.changeRole(memberId, 'Planner');
        case 'role-foreman': return this.store.changeRole(memberId, 'Foreman');
        case 'grant-admin': return this.store.setAdmin(memberId, true);
        case 'revoke-admin': return this.store.setAdmin(memberId, false);
        case 'password-link': return this.store.issuePasswordLink(memberId);
        case 'unlock': return this.store.unlock(memberId);
        case 'deactivate': return this.store.deactivate(memberId);
        case 'activate': return this.store.activate(memberId);
        default: return undefined;
      }
    })();

    this.store.closeRowMenu();

    action?.pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  onRowMenuClose(): void {
    this.store.closeRowMenu();
  }
}
